import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { PrModalComponent } from '@app/components/pr-modal/pr-modal/pr-modal.component';
import { UserDao } from '@app/dao/user/user.dao';
import { EaSimulationPacksComponent } from '@app/dashboard/sheets/shared/ea-simulation-packs/ea-simulation-packs.component';
import {
  SimulationPack,
  SimulationPackService,
} from '@app/dashboard/sheets/shared/ea-simulation-packs/simulation-pack.service';
import { InfoOption } from '@app/forms/subscription/components/info/info.component';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { infoOption } from '@app/forms/subscription/services/ref';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { leavePackStepWithoutPack } from '@app/forms/subscription/ea-subscription-forms/pledge-pack-choice';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, take } from 'rxjs';
import {
  ArbitragePackResolvedLine,
  ArbitragePackSupportsService,
} from '../services/arbitrage-pack-supports.service';

const FULL_PERCENT_TOLERANCE = 0.001;


@UntilDestroy()
@Component({
  selector: 'arbitrage-simulation-packs',
  templateUrl: './arbitrage-simulation-packs.component.html',
  styleUrls: ['./arbitrage-simulation-packs.component.scss'],
  standalone: false,
})
export class ArbitrageSimulationPacksComponent
  implements OnInit, AfterViewInit
{
  mode: 'stock' | 'flux' | 'both' = 'stock';

  optionInfo1: InfoOption = infoOption;
  packService: string = 'life_insurance';
  score = undefined;
  loading: boolean = true;
  resolving: boolean = false;
  resolutionError: string = null;
  resolvedLines: ArbitragePackResolvedLine[] = [];

  @ViewChild(EaSimulationPacksComponent, { read: SimulationPackService })
  simulationPackService: SimulationPackService;

  @ViewChild('modalPackChange', { static: true, read: PrModalComponent })
  modalPackChange: PrModalComponent;

  constructor(
    private userDao: UserDao,
    private cd: ChangeDetectorRef,
    private subscriptionFacade: SubscriptionFacadeService,
    private packSupportsService: ArbitragePackSupportsService,
  ) {}

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    this.packService = pledge.productType === 'PER' ? 'per' : 'life_insurance';
    const arbitrageType = pledge?.products?.[0]?.parameters?.arbitrageType;
    this.mode =
      arbitrageType === 'BOTH'
        ? 'both'
        : arbitrageType === 'FLUX'
          ? 'flux'
          : 'stock';
    const owners = pledge.owners;
    this.userDao
      .getAuthentified()
      .pipe(take(1))
      .subscribe(user => {
        const { score } =
          owners?.find(owner => owner.id === user.id) || owners?.[0] || {};

        this.score = score;
      });
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
    this.simulationPackService.productId = this.productId;
    this.simulationPackService
      .getPacks()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: packs => {
          this.loading = false;
          this.restoreSelectedPack(packs);
          this.cd.detectChanges();
          leavePackStepWithoutPack(packs, this.subscriptionFacade);
        },
        error: () => {
          this.loading = false;
          this.cd.detectChanges();
        },
      });
  }

  restoreSelectedPack(packs: SimulationPack[]) {
    if (this.simulationPackService.selectedPack) {
      return;
    }
    const packId =
      this.subscriptionFacade.getPledge()?.products?.[0]?.parameters
        ?.productPackId;
    const pack = packs?.find(p => p.id === (packId as any));
    if (pack) {
      this.simulationPackService.selectPack(pack);
      this.onPackSelected(pack);
    }
  }

  onPackSelected(pack: SimulationPack) {
    this.resolutionError = null;
    this.resolvedLines = [];

    if (!pack?.id) {
      return;
    }

    this.resolving = true;
    this.packSupportsService
      .resolve(pack.allocation || [], this.productId)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.resolving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: ({ lines, unresolvedIsins }) => {
          if (unresolvedIsins.length) {
            this.resolutionError = `Certains supports de cette allocation ne sont pas disponibles sur ce contrat : ${unresolvedIsins.join(
              ', ',
            )}.`;
            return;
          }
          this.resolvedLines = lines;
        },
        error: () => {
          this.resolutionError =
            "Impossible de charger la composition de l'allocation sélectionnée.";
        },
      });
  }

  get productId(): number {
    return this.subscriptionFacade.getPledge()?.products?.[0]?.productId;
  }

  get selectedPack(): SimulationPack {
    return this.simulationPackService?.selectedPack;
  }

  get totalPercentage(): number {
    return this.resolvedLines.reduce(
      (acc, line) => acc + (Number(line.allocationPercentage) || 0),
      0,
    );
  }

  get isPackComplete(): boolean {
    return (
      !!this.selectedPack?.id &&
      !this.resolving &&
      !this.resolutionError &&
      this.resolvedLines.length > 0 &&
      Math.abs(this.totalPercentage - 1) < FULL_PERCENT_TOLERANCE
    );
  }

  private allocateAmounts(fractions: number[], total: number): number[] {
    if (!fractions.length || total <= 0) {
      return fractions.map(() => 0);
    }
    const totalCents = Math.round(total * 100);
    const exact = fractions.map(f => totalCents * (Number(f) || 0));
    const rounded = exact.map(e => Math.round(e));
    const diff = totalCents - rounded.reduce((acc, v) => acc + v, 0);
    if (diff !== 0) {
      const order = exact
        .map((e, i) => ({ i, frac: e - Math.floor(e) }))
        .sort((a, b) =>
          diff > 0 ? b.frac - a.frac || a.i - b.i : a.frac - b.frac || a.i - b.i,
        );
      for (let k = 0; k < Math.abs(diff); k++) {
        rounded[order[k % order.length].i] += Math.sign(diff);
      }
    }
    return rounded.map(c => Math.max(0, c) / 100);
  }

  extract() {
    const pledge = this.subscriptionFacade.getPledge();
    const parameters: any = pledge?.products?.[0]?.parameters || {};

    const totalStock = (parameters.supportsDivestment || []).reduce(
      (acc, item) =>
        acc +
        (item?.amountRepartitionStock
          ? Number(item.amountRepartitionStock)
          : 0),
      0,
    );
    const monthlyPayment = Number(parameters.monthlyPayment) || 0;

    const fractions = this.resolvedLines.map(
      line => Number(line.allocationPercentage) || 0,
    );
    const stockAmounts = this.allocateAmounts(fractions, totalStock);
    const fluxAmounts = this.allocateAmounts(fractions, monthlyPayment);

    const packParameters: any = {
      productPackId: this.selectedPack.id,
    };

    if (this.mode !== 'flux') {
      packParameters.supportsInvestmentStock = this.resolvedLines.map(
        (line, index) => ({
          id: line.id,
          name: line.name,
          type: line.type,
          isin: line.isin,
          details: line.details,
          index,
          repartitionStock: line.allocationPercentage,
          amountRepartitionStock: stockAmounts[index],
          repartitionFlux: null,
          amountRepartitionFlux: null,
          currentAllocationAmountStock: null,
        }),
      );
    }

    if (this.mode !== 'stock') {
      packParameters.supportsInvestmentFlux = this.resolvedLines.map(
        (line, index) => ({
          id: line.id,
          name: line.name,
          type: line.type,
          isin: line.isin,
          details: line.details,
          index,
          repartitionFlux: line.allocationPercentage,
          amountRepartitionFlux: fluxAmounts[index],
          repartitionStock: null,
          amountRepartitionStock: null,
          currentAllocationAmountStock: null,
        }),
      );
    }

    return [
      {
        ownerId: null,
        productId: this.productId,
        parameters: packParameters,
      },
    ];
  }

  private get pledgeParameters(): any {
    return (
      this.subscriptionFacade.getPledge()?.products?.[0]?.parameters || {}
    );
  }

  private hasRelevantLists(parameters: any): boolean {
    const stockFilled =
      this.mode === 'flux' ||
      (parameters.supportsInvestmentStock || []).length > 0;
    const fluxFilled =
      this.mode === 'stock' ||
      (parameters.supportsInvestmentFlux || []).length > 0;
    return stockFilled && fluxFilled;
  }

  private hasCustomRepartition(parameters: any): boolean {
    const previousPack = (this.simulationPackService?.packs || []).find(
      p => p.id === (parameters.productPackId as any),
    );
    if (!previousPack) {
      return this.hasRelevantLists(parameters);
    }

    const differsFromPack = (lines: any[], percentKey: string) => {
      const allocation = previousPack.allocation || [];
      if ((lines || []).length !== allocation.length) {
        return true;
      }
      return (lines || []).some(line => {
        const packLine: any = allocation.find(
          (a: any) => a.supportIsin === line.isin,
        );
        if (!packLine) {
          return true;
        }
        return (
          Math.abs(
            (Number(line[percentKey]) || 0) -
              (Number(packLine.allocationPercentage) || 0),
          ) >= FULL_PERCENT_TOLERANCE
        );
      });
    };

    const stockDiffers =
      this.mode !== 'flux' &&
      differsFromPack(
        parameters.supportsInvestmentStock || [],
        'repartitionStock',
      );
    const fluxDiffers =
      this.mode !== 'stock' &&
      differsFromPack(
        parameters.supportsInvestmentFlux || [],
        'repartitionFlux',
      );
    return stockDiffers || fluxDiffers;
  }

  onSubmit() {
    if (!this.isPackComplete) {
      return;
    }

    const parameters = this.pledgeParameters;
    const samePackSelected =
      parameters.productPackId != null &&
      this.selectedPack.id === (parameters.productPackId as any);

    if (samePackSelected && this.hasRelevantLists(parameters)) {
      this.subscriptionFacade.nextStep();
      return;
    }

    if (
      parameters.productPackId != null &&
      !samePackSelected &&
      this.hasCustomRepartition(parameters)
    ) {
      this.modalPackChange.open();
      return;
    }

    // PRE-4896 : préremplit les 4 champs "*Autocomplete" de la slide "Taux par support" pour les
    // supports du pack sélectionné — sans quoi ils restent null pour ce chemin de sélection,
    // contrairement à la sélection libre (cf. support-form.component.ts).
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledgeWithoutForm(this as any);
  }

  confirmPackChange() {
    this.modalPackChange.close();
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledgeWithoutForm(this as any);
  }

  cancelPackChange() {
    this.modalPackChange.close();
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
